import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { PageHeader, Modal, inputCls, btnPrimary, btnGhost, EmptyState } from "../../components/bits";
import { Plus, Trash2, FileText, FileType, Pencil, Download, FileUp, Eye } from "lucide-react";
import { fmtSize } from "./AdminCirculars";

export default function AdminResources() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null); // {mode:"add"|"edit", item?}
  const [form, setForm] = useState({ title: "", category: "محاضر ونماذج" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const importRef = useRef(null);

  const load = useCallback(() => api.get("/portal/resources").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("أدخل عنوان النموذج"); return; }
    setBusy(true);
    try {
      if (modal?.mode === "edit") {
        await api.put(`/admin/resources/${modal.item.id}`, { title: form.title.trim(), category: form.category });
        toast.success("تم تحديث النموذج");
      } else {
        const f = fileRef.current?.files?.[0];
        if (f) {
          const fd = new FormData();
          fd.append("title", form.title.trim());
          fd.append("category", form.category);
          fd.append("file", f);
          await api.post("/admin/resources/upload", fd);
        } else {
          await api.post("/admin/resources", { title: form.title.trim(), category: form.category, format: "PDF", url: "#" });
        }
        toast.success("تمت إضافة النموذج للمكتبة");
      }
      setModal(null);
      setForm({ title: "", category: "محاضر ونماذج" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا النموذج؟")) return;
    await api.delete(`/admin/resources/${id}`);
    toast.success("تم الحذف");
    load();
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/resources/import", fd);
      toast.success(`استُورد ${data.imported} نموذجاً (مكرر ${data.duplicates})`);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); if (importRef.current) importRef.current.value = ""; }
  };

  if (!items) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="admin-resources-page">
      <PageHeader title="مكتبة الموارد والنماذج" sub="إدارة النماذج المتاحة لجميع المنسقين — ملفات Word وPDF">
        <input ref={importRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={doImport} data-testid="resource-import-file" />
        <button data-testid="resource-import-btn" onClick={() => importRef.current?.click()} disabled={busy} className={btnGhost}>
          <FileUp className="w-4 h-4" /> استيراد
        </button>
        <button data-testid="resource-export-btn" onClick={() => window.open(`${API}/admin/resources/export`, "_blank")} className={btnGhost}>
          <Download className="w-4 h-4" /> تصدير
        </button>
        <button data-testid="resource-add-btn" onClick={() => { setForm({ title: "", category: "محاضر ونماذج" }); setModal({ mode: "add" }); }} className={btnPrimary}>
          <Plus className="w-4 h-4" /> إضافة نموذج
        </button>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState testid="admin-resources-empty" text="المكتبة فارغة" />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`admin-resource-${r.id.slice(0, 8)}`}
              className="bg-surface rounded-2xl border border-brd shadow-md p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-turq-100 dark:bg-surface2 flex items-center justify-center shrink-0">
                {r.format === "Word" ? <FileType className="w-5 h-5 text-turq-900 dark:text-turq-300" /> : <FileText className="w-5 h-5 text-turq-900 dark:text-turq-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{r.title}</div>
                <div className="text-[11px] text-mutext mt-1">
                  {r.category} · {r.format}{r.size ? ` · ${fmtSize(r.size)}` : ""}
                </div>
                {r.storage_path && (
                  <a data-testid={`resource-view-${r.id.slice(0, 8)}`} href={`${API}/portal/resources/file/${r.id}`} target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-turq-600 hover:text-turq-900">
                    <Eye className="w-3.5 h-3.5" /> عرض الملف
                  </a>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button data-testid={`resource-edit-${r.id.slice(0, 8)}`}
                  onClick={() => { setForm({ title: r.title, category: r.category }); setModal({ mode: "edit", item: r }); }}
                  className="w-8 h-8 rounded-lg bg-beige-600 dark:bg-surface2 flex items-center justify-center">
                  <Pencil className="w-3.5 h-3.5 text-turq-900 dark:text-turq-300" />
                </button>
                <button data-testid={`resource-delete-${r.id.slice(0, 8)}`} onClick={() => remove(r.id)}
                  className="w-8 h-8 rounded-lg bg-coral-100 dark:bg-surface2 flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5 text-coral-900 dark:text-coral-300" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === "edit" ? "تعديل النموذج" : "إضافة نموذج للمكتبة"} testid="resource-form-modal">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">العنوان *</label>
            <input data-testid="resource-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">التصنيف</label>
            <select data-testid="resource-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
              {["محاضر ونماذج", "شهادات", "أدلة", "عام"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          {modal?.mode === "add" && (
            <div>
              <label className="text-xs font-bold text-mutext block mb-1.5">ملف مرفق (PDF أو Word — اختياري)</label>
              <input ref={fileRef} data-testid="resource-file-input" type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className={inputCls} />
            </div>
          )}
          <button data-testid="resource-save-btn" onClick={save} disabled={busy} className={`${btnPrimary} w-full justify-center`}>
            {busy ? "جارٍ الحفظ …" : "حفظ"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
